import React, { useCallback, useEffect, useState } from 'react';

import { Navigate } from 'react-router-dom';

import { toast } from 'react-toastify';

import { useAuth } from '../context/AuthContext';

import { creditsService } from '../services/api';

import { CreditPackage } from '../types';

import HostelCreditsCell from '../components/HostelCreditsCell';

import './Credits.css';



const formatInr = (value: number) =>

  `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;



const Credits: React.FC = () => {

  const { hostel } = useAuth();

  const [loading, setLoading] = useState(true);

  const [emailCredits, setEmailCredits] = useState(0);

  const [whatsappCredits, setWhatsappCredits] = useState(0);

  const [shopEnabled, setShopEnabled] = useState(false);
  const [pageEnabled, setPageEnabled] = useState(true);
  const [emailVisible, setEmailVisible] = useState(true);
  const [whatsappVisible, setWhatsappVisible] = useState(true);

  const [packages, setPackages] = useState<CreditPackage[]>([]);

  const [buyingId, setBuyingId] = useState<number | null>(null);



  const load = useCallback(async () => {

    if (!hostel?.id) return;

    try {

      setLoading(true);

      const [creditsData, shop] = await Promise.all([

        creditsService.getHostelCredits(hostel.id),

        creditsService.getCreditShop(),

      ]);

      setEmailCredits(creditsData.emailCredits);

      setWhatsappCredits(creditsData.whatsappCredits);

      setPageEnabled(shop.creditsPageEnabled !== false);
      setEmailVisible(shop.emailCreditsVisible !== false);
      setWhatsappVisible(shop.whatsappCreditsVisible !== false);
      setShopEnabled(shop.enabled);
      setPackages(shop.packages);

    } catch {

      toast.error('Could not load credits');

    } finally {

      setLoading(false);

    }

  }, [hostel?.id]);



  useEffect(() => {

    void load();

  }, [load]);



  const buyPackage = async (pkg: CreditPackage) => {

    if (!hostel?.id) return;

    const summary = [

      pkg.emailCredits > 0 ? `${pkg.emailCredits} email` : null,

      pkg.whatsappCredits > 0 ? `${pkg.whatsappCredits} WhatsApp` : null,

    ]

      .filter(Boolean)

      .join(' + ');



    if (

      !window.confirm(

        `Purchase "${pkg.name}" for ${formatInr(pkg.priceInr)}?\n\nYou will receive: ${summary} credits.`,

      )

    ) {

      return;

    }



    setBuyingId(pkg.id);

    try {

      const result = await creditsService.purchaseCreditPackage(hostel.id, pkg.id);

      setEmailCredits(result.credits.emailCredits);

      setWhatsappCredits(result.credits.whatsappCredits);

      toast.success(`${result.purchase.packageName} purchased — credits added to your balance.`);

    } catch (err: unknown) {

      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;

      toast.error(msg || 'Purchase failed');

    } finally {

      setBuyingId(null);

    }

  };



  if (!hostel?.id) {

    return <p className="credits-page__empty">Select an active hostel to view credits.</p>;

  }

  const creditsPageAccessible = pageEnabled && (emailVisible || whatsappVisible);

  if (!loading && !creditsPageAccessible) {
    return <Navigate to="/dashboard" replace />;
  }

  return (

    <div className="credits-page">

      <header className="credits-page__head">

        <h1>Credits</h1>

        <p>Available messaging balance for <strong>{hostel.name}</strong></p>

      </header>



      {loading ? (

        <p className="credits-page__loading">Loading…</p>

      ) : (

        <>

          <div className={`credits-grid${emailVisible && whatsappVisible ? '' : ' credits-grid--single'}`}>

            {emailVisible ? (
              <article className={`credits-card credits-card--email${emailCredits > 0 ? '' : ' credits-card--empty'}`}>

                <span className="credits-card__label">Email credits</span>

                <strong className="credits-card__value">{emailCredits.toLocaleString('en-IN')}</strong>

                <p className="credits-card__hint">

                  {emailCredits > 0

                    ? 'Invoice emails will be sent when tenants have an email on file.'

                    : 'No email credits — invoice emails are paused until credits are added.'}

                </p>

              </article>
            ) : null}

            {whatsappVisible ? (
              <article className={`credits-card credits-card--whatsapp${whatsappCredits > 0 ? '' : ' credits-card--empty'}`}>

                <span className="credits-card__label">WhatsApp credits</span>

                <strong className="credits-card__value">{whatsappCredits.toLocaleString('en-IN')}</strong>

                <p className="credits-card__hint">

                  {whatsappCredits > 0

                    ? 'Payment reminders and payment confirmations use WhatsApp/SMS.'

                    : 'No WhatsApp credits — automated reminders are paused.'}

                </p>

              </article>
            ) : null}

          </div>



          {shopEnabled && packages.length > 0 ? (

            <section className="credits-shop">

              <header className="credits-shop__head">

                <h2>Buy credit packages</h2>

                <p>Choose a pack to top up email and WhatsApp credits instantly.</p>

              </header>

              <div className="credits-shop__grid">

                {packages.map((pkg) => (

                  <article key={pkg.id} className="credits-package-card">

                    <div className="credits-package-card__top">

                      <h3>{pkg.name}</h3>

                      <strong className="credits-package-card__price">{formatInr(pkg.priceInr)}</strong>

                    </div>

                    {pkg.description ? (

                      <p className="credits-package-card__desc">{pkg.description}</p>

                    ) : null}

                    <HostelCreditsCell

                      emailCredits={pkg.emailCredits}

                      whatsappCredits={pkg.whatsappCredits}

                      showEmail={emailVisible}

                      showWhatsapp={whatsappVisible}

                    />

                    <button

                      type="button"

                      className="credits-package-card__buy"

                      onClick={() => void buyPackage(pkg)}

                      disabled={buyingId === pkg.id}

                    >

                      {buyingId === pkg.id ? 'Processing…' : 'Buy package'}

                    </button>

                  </article>

                ))}

              </div>

            </section>

          ) : null}



          {!shopEnabled ? (

            <p className="credits-shop-disabled">

              Self-service credit purchases are currently disabled. Contact your platform admin for more credits.

            </p>

          ) : null}

        </>

      )}

    </div>

  );

};



export default Credits;


